import { motion } from "framer-motion";
import { CalendarCheck, CreditCard, XCircle, Truck, Wheat, Palette, Refrigerator, Heart } from "lucide-react";
import { BotanicalDivider } from "../components/Botanical";

const ease = [0.22, 1, 0.36, 1];

const POLICIES = [
  {
    icon: CalendarCheck,
    title: "Orders & Booking",
    points: [
      "A minimum of 7 days' notice is required for cupcake and brownie orders.",
      "Celebration cakes require at least 2 weeks' notice.",
      "Rush orders may be accepted depending on availability and may incur an additional charge.",
    ],
  },
  {
    icon: CreditCard,
    title: "Deposits & Payments",
    points: [
      "A 50% non-refundable deposit is required to secure your booking.",
      "The remaining balance must be paid no later than 48 hours before collection or delivery.",
      "Orders will not be completed until full payment has been received.",
    ],
  },
  {
    icon: XCircle,
    title: "Cancellations",
    points: [
      "Deposits are non-refundable.",
      "Cancellations made within 48 hours of collection or delivery are not eligible for a refund.",
    ],
  },
  {
    icon: Truck,
    title: "Collection & Delivery",
    points: [
      "Please arrive at your agreed collection time.",
      "Delivery is available within selected areas and is charged separately.",
      "Velvet Bakes is not responsible for damage once an order has been collected.",
    ],
  },
  {
    icon: Wheat,
    title: "Allergies",
    points: [
      "Products may contain or come into contact with milk, eggs, wheat (gluten), soya, nuts and other allergens.",
      "Please inform us of any allergies before placing your order.",
    ],
  },
  {
    icon: Palette,
    title: "Design Disclaimer",
    points: [
      "Every cake and treat is handmade, so slight variations in colour and decoration may occur.",
      "Inspiration photos are used as a guide and exact replicas cannot be guaranteed.",
    ],
  },
  {
    icon: Refrigerator,
    title: "Storage",
    points: [
      "Store cakes in a cool, dry place away from direct sunlight.",
      "Refrigerate products containing fresh cream or perishable fillings.",
      "Best enjoyed within 2–3 days.",
    ],
  },
];

const Policies = () => (
  <section id="policies" className="relative py-24 md:py-32" data-testid="policies-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.9, ease }}
        className="text-center mb-16"
      >
        <p className="text-xs uppercase tracking-[0.3em] font-semibold text-[#8B5D6C] mb-4">Business Policy</p>
        <h2 className="font-serif text-4xl md:text-5xl text-[#4A2834]">The fine print, written kindly</h2>
        <BotanicalDivider className="w-64 mx-auto mt-6 text-[#C5A059]" />
        <p className="mt-6 max-w-2xl mx-auto text-[#70505D] text-sm md:text-base leading-relaxed">
          Everything here exists so your bake arrives perfect and your celebration runs
          smoothly. Any questions — just ask before you order.
        </p>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6" data-testid="policies-grid">
        {POLICIES.map((policy, i) => (
          <motion.div
            key={policy.title}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.7, delay: (i % 3) * 0.1, ease }}
            className={`bg-[#F3EEE6]/80 border border-[#C5A059]/25 rounded-3xl p-8 hover:border-[#C5A059]/60 hover:shadow-xl hover:shadow-[#8B5D6C]/10 transition-all duration-500 ${
              i === POLICIES.length - 1 ? "md:col-span-2 lg:col-span-1" : ""
            }`}
            data-testid={`policy-card-${policy.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
          >
            <div className="w-12 h-12 rounded-full border border-[#C5A059]/60 flex items-center justify-center text-[#C5A059] mb-6">
              <policy.icon size={20} />
            </div>
            <h3 className="font-serif text-2xl text-[#4A2834]">{policy.title}</h3>
            <span className="gold-rule w-10 block my-4" />
            <ul className="space-y-3">
              {policy.points.map((point, j) => (
                <li key={j} className="flex gap-3 text-sm text-[#70505D] leading-relaxed">
                  <span className="mt-[7px] w-1 h-1 rounded-full bg-[#B87D8E] shrink-0" />
                  {point}
                </li>
              ))}
            </ul>
          </motion.div>
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8, ease }}
        className="mt-14 text-center"
        data-testid="policies-thanks"
      >
        <p className="font-serif italic text-2xl md:text-3xl text-[#8B5D6C] inline-flex items-center gap-3">
          <Heart size={18} className="text-[#C5A059] fill-[#C5A059]" />
          Thank you for supporting Velvet Bakes
          <Heart size={18} className="text-[#C5A059] fill-[#C5A059]" />
        </p>
        <p className="text-sm text-[#987885] mt-2">We look forward to creating something special for you.</p>
      </motion.div>
    </div>
  </section>
);

export default Policies;
